import React, { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import {
  TabDocument,
  documentLabel,
  shortDate,
  statusClass,
  statusLabel,
} from '../../../config';
import './document-nav.scss';

/**
 * A site inspection as returned by get_project_inspections /
 * get_stage_inspections. Only the fields this panel shows.
 */
interface NavInspection {
  id: number;
  sequence_number: number | null;
  inspection_date: string;
  description: string;
  /** Set when this record IS an issued site instruction -- see the dedupe below. */
  document_id: string | null;
}

export type DocumentNavTab = 'documents' | 'inspections';

interface DocumentNavProps {
  /** The document being edited: its row is highlighted and inert. */
  currentDocumentId: string;
  /** The job the panel lists. Exactly one is set -- documents are a strict XOR. */
  projectId: number | null;
  stageId: number | null;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  activeTab: DocumentNavTab;
  onActiveTabChange: (tab: DocumentNavTab) => void;
  onOpenDocument: (documentId: string) => void;
  /**
   * Pinned to the foot of the panel, collapsed or open: the editor's delete
   * button, which used to float over the page area's corner.
   */
  footer?: React.ReactNode;
}

const TABS: Array<{ id: DocumentNavTab; label: string; icon: React.ReactNode }> = [
  {
    id: 'documents',
    label: 'Documents',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9.5 1.5H4a1.5 1.5 0 0 0-1.5 1.5v10A1.5 1.5 0 0 0 4 14.5h8a1.5 1.5 0 0 0 1.5-1.5V5.5z" />
        <path d="M9.5 1.5v4h4" />
      </svg>
    ),
  },
  {
    id: 'inspections',
    label: 'Inspections',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="7" cy="7" r="4.5" />
        <line x1="10.5" y1="10.5" x2="14" y2="14" />
      </svg>
    ),
  },
];

/**
 * The builder's left-hand panel: the other documents and inspection records on
 * this job, so the one being written can be checked against them without
 * leaving it.
 *
 * The data comes from the two RPCs the project modal already uses, so the rows
 * obey the same visibility rules as the Documents tab -- get_project_documents
 * filters by the template's can_view, and this panel does no filtering of its
 * own that could disagree with it.
 */
const DocumentNav: React.FC<DocumentNavProps> = ({
  currentDocumentId,
  projectId,
  stageId,
  collapsed,
  onCollapsedChange,
  activeTab,
  onActiveTabChange,
  onOpenDocument,
  footer,
}) => {
  const supabase = useSupabaseClient();

  /**
   * Cached on the job, not on the document.
   *
   * The editor is keyed on documentId and so remounts on every row click, but
   * every row in this panel is on the SAME job -- so react-query repaints the
   * list from cache and the panel never blinks while the next document loads.
   */
  const { data, isLoading } = useQuery({
    queryKey: ['document_siblings', projectId, stageId],
    enabled: projectId != null || stageId != null,
    queryFn: async () => {
      const [docsRes, inspRes] = await Promise.all([
        supabase.rpc('get_project_documents', { p_project_id: projectId, p_stage_id: stageId }),
        stageId
          ? supabase.rpc('get_stage_inspections', { p_stage_id: stageId })
          : supabase.rpc('get_project_inspections', { p_project_id: projectId }),
      ]);
      if (docsRes.error) throw docsRes.error;
      if (inspRes.error) throw inspRes.error;
      return {
        documents: (docsRes.data as TabDocument[]) || [],
        inspections: ((inspRes.data as { inspections?: NavInspection[] } | null)?.inspections) || [],
      };
    },
  });

  const allDocuments = useMemo(() => data?.documents ?? [], [data]);

  /**
   * Reports and certificates. Site instructions are deliberately NOT here --
   * an instruction IS the inspection record, so it belongs with the rest of the
   * SI sequence on the other tab rather than in a third place of its own.
   *
   * Split on the template's creates_inspection flag, carried by
   * document_list_view, rather than on the category's display string: the
   * categories are reference data a manager can rename.
   */
  const documents = useMemo(
    () => allDocuments.filter(d => !d.creates_inspection),
    [allDocuments]
  );

  /**
   * The inspection records that are not already listed as documents.
   *
   * An issued site instruction owns an inspections row written by
   * sync_document_inspection(), so it would otherwise appear twice.
   *
   * The test is membership of the document list, NOT `document_id != null`:
   * the two RPCs have different security models -- get_project_documents is
   * SECURITY DEFINER and applies the template's can_view, while the inspection
   * RPCs are SECURITY INVOKER over RLS. A viewer outside can_view gets the
   * inspection row but not the document, and keying on the column would drop
   * that SI from the panel entirely, leaving a hole in the numbering.
   */
  const inspections = useMemo(() => {
    const listed = new Set(allDocuments.map(d => d.id));
    return (data?.inspections ?? []).filter(i => !i.document_id || !listed.has(i.document_id));
  }, [data, allDocuments]);

  /**
   * The job's inspection record, in one sequence: the site instructions and the
   * records added by hand, newest SI first.
   *
   * They share one number sequence per job (see "One SI number sequence per
   * job" in DEVELOPMENT.md), so interleaving them by that number is the order
   * they were actually raised in -- and the hand-added ones, which are the
   * legacy way of recording this, stop being a separate list to remember.
   */
  const inspectionEntries = useMemo(() => {
    const fromDocuments = allDocuments
      .filter(d => d.creates_inspection)
      .map(d => ({ kind: 'document' as const, key: d.id, seq: d.sequence_number, doc: d }));
    const fromRecords = inspections.map(i => ({
      kind: 'record' as const, key: `i${i.id}`, seq: i.sequence_number, insp: i,
    }));
    return [...fromDocuments, ...fromRecords].sort(
      (a, b) => (b.seq ?? -1) - (a.seq ?? -1)
    );
  }, [allDocuments, inspections]);

  const loading = isLoading;

  /**
   * One heading per category, in the order the RPC returns them -- the same
   * order the Documents tab shows, which is document_templates.display_order.
   */
  const groups = useMemo(() => {
    const byCategory = new Map<string, TabDocument[]>();
    documents.forEach(doc => {
      const existing = byCategory.get(doc.category);
      if (existing) existing.push(doc);
      else byCategory.set(doc.category, [doc]);
    });
    // Newest first WITHIN the group. The RPC sorts by display_order before
    // date, so a category holding two templates comes back interleaved by
    // template rather than by date -- fine for the Documents tab, which shows
    // the templates too, wrong for a list you are scanning for the last thing
    // issued.
    return Array.from(byCategory.entries()).map(([category, docs]) => ({
      category,
      docs: [...docs].sort((a, b) => b.document_date.localeCompare(a.document_date)),
    }));
  }, [documents]);

  const selectTab = useCallback((tab: DocumentNavTab) => {
    onActiveTabChange(tab);
    onCollapsedChange(false);
  }, [onActiveTabChange, onCollapsedChange]);

  return (
    <aside className={`editor-docnav ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => onCollapsedChange(!collapsed)}
          title={collapsed ? 'Expand panel' : 'Collapse panel'}
          aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" />
            <line x1="6" y1="2.5" x2="6" y2="13.5" />
          </svg>
        </button>
        {!collapsed && (
          <div className="sidebar-tabs" role="tablist">
            {TABS.map(tab => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => onActiveTabChange(tab.id)}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {collapsed && (
        <div className="sidebar-rail">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`rail-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => selectTab(tab.id)}
              title={tab.label}
              aria-label={tab.label}
            >
              {tab.icon}
            </button>
          ))}
        </div>
      )}

      {!collapsed && (
        <div className="sidebar-content">
          {loading ? (
            <p className="panel-hint">Loading…</p>
          ) : activeTab === 'documents' ? (
            groups.length === 0 ? (
              // Not "no documents": a job can be full of site instructions and
              // still have nothing here, and saying otherwise would read as a
              // bug to someone looking at six of them on the next tab.
              <p className="is-empty">No reports or certificates on this job yet.</p>
            ) : (
              groups.map(group => (
                <div key={group.category} className="docnav-group">
                  <div className="panel-heading">
                    <span className="panel-heading__title">{group.category}</span>
                    <span className="panel-heading__meta">{group.docs.length}</span>
                  </div>
                  <div className="docnav-list">
                    {group.docs.map(doc => {
                      const isCurrent = doc.id === currentDocumentId;
                      return (
                        <div
                          key={doc.id}
                          className={`docnav-row ${isCurrent ? 'docnav-row--current' : ''}`}
                          role={isCurrent ? undefined : 'button'}
                          tabIndex={isCurrent ? -1 : 0}
                          onClick={isCurrent ? undefined : () => onOpenDocument(doc.id)}
                          onKeyDown={e => {
                            if (isCurrent) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onOpenDocument(doc.id);
                            }
                          }}
                        >
                          <div className="docnav-row__top">
                            <span className="docnav-row__title">{documentLabel(doc)}</span>
                            {isCurrent && <span className="docnav-row__current">Current</span>}
                          </div>
                          <div className="docnav-row__bottom">
                            <span className="docnav-row__date">{shortDate(doc.document_date)}</span>
                            <span className={`docnav-row__status ${statusClass[doc.status]}`}>
                              {statusLabel[doc.status]}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )
          ) : inspectionEntries.length === 0 ? (
            <p className="is-empty">No site inspection records or inspections on this job yet.</p>
          ) : (
            <div className="docnav-group">
              <div className="panel-heading">
                <span className="panel-heading__title">Inspection record</span>
                <span className="panel-heading__meta">{inspectionEntries.length}</span>
              </div>
              <div className="docnav-list">
                {inspectionEntries.map(entry => {
                  // A hand-added record: no document to open, so it is inert.
                  // These are how inspections used to be kept and are becoming
                  // rare, but they hold SI numbers and the sequence has to read
                  // continuously.
                  if (entry.kind === 'record') {
                    return (
                      <div key={entry.key} className="docnav-row docnav-row--static">
                        <div className="docnav-row__top">
                          <span className="docnav-row__title">
                            {entry.seq != null ? `SI ${entry.seq}` : 'Inspection'}
                          </span>
                          <span className="docnav-row__current">Record</span>
                        </div>
                        <div className="docnav-row__bottom">
                          <span className="docnav-row__date">{shortDate(entry.insp.inspection_date)}</span>
                        </div>
                        <p className="docnav-row__note">{entry.insp.description}</p>
                      </div>
                    );
                  }

                  const doc = entry.doc;
                  const isCurrent = doc.id === currentDocumentId;
                  return (
                    <div
                      key={entry.key}
                      className={`docnav-row ${isCurrent ? 'docnav-row--current' : ''}`}
                      role={isCurrent ? undefined : 'button'}
                      tabIndex={isCurrent ? -1 : 0}
                      onClick={isCurrent ? undefined : () => onOpenDocument(doc.id)}
                      onKeyDown={e => {
                        if (isCurrent) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpenDocument(doc.id);
                        }
                      }}
                    >
                      <div className="docnav-row__top">
                        <span className="docnav-row__title" title={documentLabel(doc)}>
                          {documentLabel(doc)}
                        </span>
                        {isCurrent && <span className="docnav-row__current">Current</span>}
                      </div>
                      <div className="docnav-row__bottom">
                        <span className="docnav-row__date">{shortDate(doc.document_date)}</span>
                        <span className={`docnav-row__status ${statusClass[doc.status]}`}>
                          {statusLabel[doc.status]}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {footer && <div className="sidebar-footer">{footer}</div>}
    </aside>
  );
};

export default DocumentNav;
